import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { StreamableFile } from "@nestjs/common";
import { ErrorMessageSchema } from "../schemas/error.schema";
import { CounterpartyDirectionOverride } from "../helpers/counterparties";
import { Counterparty, FederalTaxClassification } from "../types/counterparty";
import {
    deleteCounterpartyConflictResponseSchema,
    deleteCounterpartyQuerySchema,
    downloadCounterpartiesQuerySchema,
    getCounterpartiesQuerySchema,
    getCounterpartiesResponseSchema,
    mergeCounterpartiesBodySchema,
    mergeCounterpartiesConflictResponseSchema,
    optionalCounterpartyEmailSchema,
    optionalCounterpartyNoteSchema,
    optionalCounterpartyTextSchema,
    optionalCounterpartyWebsiteSchema,
    rejectInvoiceCounterpartiesOnlyWithUsageStats,
    requireUsageStatsForExclusionFilters,
} from "../schemas/counterparty.schema";
import {
    counterpartyContactSchema,
    createCounterpartyContactBodySchema,
    updateCounterpartyContactBodySchema,
} from "../schemas/counterpartyContact.schema";
import {
    counterpartyAddressSchema,
    createCounterpartyAddressBodySchema,
    updateCounterpartyAddressBodySchema,
} from "../schemas/counterpartyAddress.schema";
import { counterpartyDetailsSchema } from "../schemas/counterpartyDetails.schema";
import {
    addCounterpartyFieldReviewsBodySchema,
    counterpartyFieldReviewSchema,
    genericCounterpartyReviewFieldEnum,
} from "../schemas/counterpartyFieldReview.schema";
import {
    counterpartyTinErrorSchema,
    counterpartyTinExportGrantPathParamsSchema,
    counterpartyTinPathParamsSchema,
    counterpartyTinStatusResponseSchema,
    createCounterpartyTinExportGrantBodySchema,
    createCounterpartyTinGrantBodySchema,
    createCounterpartyTinGrantResponseSchema,
    reviewCounterpartyTinBodySchema,
    reviewCounterpartyTinResponseSchema,
    revokeCounterpartyTinReviewResponseSchema,
} from "../schemas/counterpartyTin.schema";

const c = initContract();

const optionalFields = {
    companyName: optionalCounterpartyTextSchema,
    email: optionalCounterpartyEmailSchema,
    cc: optionalCounterpartyEmailSchema,
    bcc: optionalCounterpartyEmailSchema,
    phoneNumber: z.string().optional().nullable(),
    website: optionalCounterpartyWebsiteSchema,
    title: optionalCounterpartyTextSchema,
    firstName: optionalCounterpartyTextSchema,
    middleName: optionalCounterpartyTextSchema,
    lastName: optionalCounterpartyTextSchema,
    suffix: optionalCounterpartyTextSchema,
    street: z.string().optional().nullable(),
    street2: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    postalCode: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
    isEligible1099: z.boolean().optional().nullable(),
    federalTaxClassification: z
        .nativeEnum(FederalTaxClassification)
        .nullable()
        .optional(),
    note: optionalCounterpartyNoteSchema,
};
const updatePayload = z.object({
    name: z.string().min(1).optional(),
    ...optionalFields,
    directionOverride: z
        .nativeEnum(CounterpartyDirectionOverride)
        .nullable()
        .optional(),
    isDisabled: z.boolean().optional(),
});

export interface CounterpartyCreationResponse {
    counterparty: Counterparty;
    revealedOnTransactionsNumber?: number;
}

export const counterpartyContract = c.router(
    {
        createCounterparty: {
            method: "POST",
            path: "",
            body: z.object({
                name: z.string().min(1),
                workspaceId: z.string().uuid(),
                ...optionalFields,
                directionOverride: z
                    .nativeEnum(CounterpartyDirectionOverride)
                    .nullable()
                    .optional(),
            }),
            responses: {
                200: c.type<CounterpartyCreationResponse>(),
                201: c.type<CounterpartyCreationResponse>(),
                400: c.type<{ message: string }>(),
            },
        },
        updateCounterparty: {
            method: "PATCH",
            path: "/:workspaceId/:counterpartyId",
            body: updatePayload,
            responses: {
                201: c.type<{ counterparty: Counterparty }>(),
                400: c.type<{ message: string }>(),
            },
        },
        deleteCounterparty: {
            method: "DELETE",
            path: "/:workspaceId/:counterpartyId",
            query: deleteCounterpartyQuerySchema,
            responses: {
                200: c.noBody(),
                400: c.type<{ message: string }>(),
                409: deleteCounterpartyConflictResponseSchema,
            },
        },
        mergeCounterparties: {
            method: "POST",
            path: "/:workspaceId/merge",
            body: mergeCounterpartiesBodySchema,
            responses: {
                200: c.noBody(),
                400: c.type<{ message: string }>(),
                409: mergeCounterpartiesConflictResponseSchema,
            },
        },
        copyGlobalCounterparty: {
            method: "POST",
            path: "/copy-global-to-local",
            body: z.object({
                globalCounterpartyId: z.string().uuid(),
                workspaceId: z.string().uuid(),
                update: updatePayload.optional(),
            }),
            responses: {
                201: c.type<{ localCounterparty: Counterparty }>(),
                400: c.type<{ message: string }>(),
            },
        },
        getCounterparties: {
            method: "GET",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: getCounterpartiesQuerySchema
                .superRefine(rejectInvoiceCounterpartiesOnlyWithUsageStats)
                .superRefine(requireUsageStatsForExclusionFilters),
            responses: {
                200: getCounterpartiesResponseSchema,
                404: c.noBody(),
            },
        },
        downloadCounterparties: {
            method: "GET",
            path: "/:workspaceId/download",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: downloadCounterpartiesQuerySchema
                .superRefine(rejectInvoiceCounterpartiesOnlyWithUsageStats)
                .superRefine(requireUsageStatsForExclusionFilters),
            responses: {
                200: c.type<StreamableFile>(),
                404: c.noBody(),
            },
        },
        getCounterpartyDetails: {
            method: "GET",
            path: "/:workspaceId/:counterpartyId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
            }),
            responses: {
                200: counterpartyDetailsSchema,
                404: ErrorMessageSchema,
            },
        },
        createCounterpartyContact: {
            method: "POST",
            path: "/:workspaceId/:counterpartyId/contacts",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
            }),
            body: createCounterpartyContactBodySchema,
            responses: {
                201: z.object({ contact: counterpartyContactSchema }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateCounterpartyContact: {
            method: "PATCH",
            path: "/:workspaceId/:counterpartyId/contacts/:contactId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
                contactId: z.string().uuid(),
            }),
            body: updateCounterpartyContactBodySchema,
            responses: {
                200: z.object({ contact: counterpartyContactSchema }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteCounterpartyContact: {
            method: "DELETE",
            path: "/:workspaceId/:counterpartyId/contacts/:contactId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
                contactId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                204: c.noBody(),
                404: ErrorMessageSchema,
            },
        },
        setPrimaryCounterpartyContact: {
            method: "POST",
            path: "/:workspaceId/:counterpartyId/contacts/:contactId/primary",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
                contactId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: z.object({ contact: counterpartyContactSchema }),
                404: ErrorMessageSchema,
            },
        },
        createCounterpartyAddress: {
            method: "POST",
            path: "/:workspaceId/:counterpartyId/addresses",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
            }),
            body: createCounterpartyAddressBodySchema,
            responses: {
                201: z.object({ address: counterpartyAddressSchema }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateCounterpartyAddress: {
            method: "PATCH",
            path: "/:workspaceId/:counterpartyId/addresses/:addressId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
                addressId: z.string().uuid(),
            }),
            body: updateCounterpartyAddressBodySchema,
            responses: {
                200: z.object({ address: counterpartyAddressSchema }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteCounterpartyAddress: {
            method: "DELETE",
            path: "/:workspaceId/:counterpartyId/addresses/:addressId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
                addressId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                204: c.noBody(),
                404: ErrorMessageSchema,
            },
        },
        setPrimaryCounterpartyAddress: {
            method: "POST",
            path: "/:workspaceId/:counterpartyId/addresses/:addressId/primary",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
                addressId: z.string().uuid(),
            }),
            body: c.noBody(),
            responses: {
                200: z.object({ address: counterpartyAddressSchema }),
                404: ErrorMessageSchema,
            },
        },
        addFieldReviews: {
            method: "POST",
            path: "/:workspaceId/:counterpartyId/field-reviews",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
            }),
            body: addCounterpartyFieldReviewsBodySchema,
            responses: {
                200: z.object({
                    reviews: z.array(counterpartyFieldReviewSchema),
                }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        revokeFieldReview: {
            method: "DELETE",
            path: "/:workspaceId/:counterpartyId/field-reviews/:field",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                counterpartyId: z.string().uuid(),
                field: genericCounterpartyReviewFieldEnum,
            }),
            body: c.noBody(),
            responses: {
                200: z.object({ deleted: z.boolean() }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getCounterpartyTin: {
            method: "GET",
            path: "/:workspaceId/:counterpartyId/tin",
            pathParams: counterpartyTinPathParamsSchema,
            responses: {
                200: counterpartyTinStatusResponseSchema,
                403: counterpartyTinErrorSchema,
                404: counterpartyTinErrorSchema,
            },
        },
        reviewCounterpartyTin: {
            method: "POST",
            path: "/:workspaceId/:counterpartyId/tin/review",
            pathParams: counterpartyTinPathParamsSchema,
            body: reviewCounterpartyTinBodySchema,
            responses: {
                200: reviewCounterpartyTinResponseSchema,
                403: counterpartyTinErrorSchema,
                404: counterpartyTinErrorSchema,
                409: counterpartyTinErrorSchema,
            },
        },
        revokeCounterpartyTinReview: {
            method: "DELETE",
            path: "/:workspaceId/:counterpartyId/tin/review",
            pathParams: counterpartyTinPathParamsSchema,
            body: c.noBody(),
            responses: {
                200: revokeCounterpartyTinReviewResponseSchema,
                403: counterpartyTinErrorSchema,
                404: counterpartyTinErrorSchema,
            },
        },
        createCounterpartyTinGrant: {
            method: "POST",
            path: "/:workspaceId/:counterpartyId/tin/grants",
            pathParams: counterpartyTinPathParamsSchema,
            body: createCounterpartyTinGrantBodySchema,
            responses: {
                201: createCounterpartyTinGrantResponseSchema,
                403: counterpartyTinErrorSchema,
                404: counterpartyTinErrorSchema,
                409: counterpartyTinErrorSchema,
                429: counterpartyTinErrorSchema,
            },
        },
        createCounterpartyTinExportGrant: {
            method: "POST",
            path: "/:workspaceId/tin-export-grants",
            pathParams: counterpartyTinExportGrantPathParamsSchema,
            body: createCounterpartyTinExportGrantBodySchema,
            responses: {
                201: createCounterpartyTinGrantResponseSchema,
                403: counterpartyTinErrorSchema,
                404: counterpartyTinErrorSchema,
                429: counterpartyTinErrorSchema,
            },
        },
        clearCounterpartyTin: {
            method: "DELETE",
            path: "/:workspaceId/:counterpartyId/tin",
            pathParams: counterpartyTinPathParamsSchema,
            body: c.noBody(),
            responses: {
                200: c.noBody(),
                403: counterpartyTinErrorSchema,
                404: counterpartyTinErrorSchema,
                429: counterpartyTinErrorSchema,
            },
        },
    },
    {
        pathPrefix: "/counterparty",
    },
);
